import type {
  JourneyDetail,
  JourneyDetailStep,
  JourneyDraftInviteRequest,
  JourneyLinkRequest,
  JourneyMeetingOption,
  JourneyMeetingOptionsResponse,
  JourneyPendingInvite,
  JourneyPlanRequest,
  JourneyRecordPastRequest,
  JourneyStepMeetingResponse,
  JourneyStepProposal,
  JourneyStepScreening,
  JourneyView,
  JourneyViewMeeting,
  JourneyViewStep,
  JourneyViewsResponse,
} from '@wundo/shared/journey.ts';
import { apiFetch, apiFetchJson } from './api.ts';

export type {
  JourneyDetail,
  JourneyDetailStep,
  JourneyMeetingOption,
  JourneyMeetingOptionsResponse,
  JourneyPendingInvite,
  JourneyStepProposal,
  JourneyStepScreening,
  JourneyView,
  JourneyViewMeeting,
  JourneyViewStep,
};

/** The journeys a manager follows, in the order the backend puts them: running first. */
export async function fetchUserJourneys(userId: string): Promise<JourneyView[]> {
  const response = await apiFetchJson<JourneyViewsResponse>(`/users/${userId}/journeys`);
  return response.data;
}

export async function fetchUserJourney(
  userId: string,
  journeyId: string,
): Promise<JourneyDetail> {
  return apiFetchJson<JourneyDetail>(`/users/${userId}/journeys/${journeyId}`);
}

function stepPath(userId: string, journeyId: string, stepId: string): string {
  return `/users/${userId}/journeys/${journeyId}/steps/${stepId}`;
}

/** What a step can be linked to: the meetings with the employee, and what `query` finds by title or day. */
export function fetchJourneyMeetingOptions(
  userId: string,
  journeyId: string,
  stepId: string,
  query: string,
): Promise<JourneyMeetingOptionsResponse> {
  const search =
    query.trim() === '' ? '' : `?${new URLSearchParams({ q: query.trim() }).toString()}`;
  return apiFetchJson<JourneyMeetingOptionsResponse>(
    `${stepPath(userId, journeyId, stepId)}/meeting-options${search}`,
  );
}

/**
 * Look in the calendar for a later step's conversation, when its card is
 * opened. The first open step needs no call: the journey carries its screening.
 */
export function screenJourneyStep(
  userId: string,
  journeyId: string,
  stepId: string,
): Promise<JourneyStepScreening> {
  return apiFetchJson<JourneyStepScreening>(
    `${stepPath(userId, journeyId, stepId)}/actions/screen`,
    { method: 'POST' },
  );
}

/** Refused (validation) when the step already has a meeting or the journey was stopped. */
export async function linkJourneyStep(
  userId: string,
  journeyId: string,
  stepId: string,
  meetingId: string,
): Promise<void> {
  const body: JourneyLinkRequest = { meeting_id: meetingId };
  await apiFetch(`${stepPath(userId, journeyId, stepId)}/actions/link`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** The undo right after linking, and "Ontkoppelen" later: the meeting goes back to how it was. */
export async function unlinkJourneyStep(
  userId: string,
  journeyId: string,
  stepId: string,
): Promise<void> {
  await apiFetch(`${stepPath(userId, journeyId, stepId)}/actions/unlink`, {
    method: 'POST',
  });
}

/**
 * Write the step's conversation down in Wundo and link it, for a manager
 * without a calendar. Refused (validation) with a calendar connected, or for a
 * moment in the past. Resolves to the meeting written down.
 */
export async function planJourneyStep(
  userId: string,
  journeyId: string,
  stepId: string,
  startsAt: Date,
): Promise<string> {
  const body: JourneyPlanRequest = { starts_at: startsAt.toISOString() };
  const response = await apiFetchJson<JourneyStepMeetingResponse>(
    `${stepPath(userId, journeyId, stepId)}/actions/plan`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
  return response.meeting_id;
}

/**
 * Keep the invite just drafted for the step, for a manager with a calendar:
 * the sync links it once sent. Refused (validation) without a calendar, or once
 * the step has a meeting or the journey stopped.
 */
export async function draftJourneyInvite(
  userId: string,
  journeyId: string,
  stepId: string,
  startsAt: Date,
): Promise<void> {
  const body: JourneyDraftInviteRequest = { starts_at: startsAt.toISOString() };
  await apiFetch(`${stepPath(userId, journeyId, stepId)}/actions/draft-invite`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** A conversation that already took place on `date` (YYYY-MM-DD). */
export interface HeldJourneyConversation {
  date: string;
  /** Pasted notes; they become the material of its verslag. */
  reportText: string | null;
}

/** "Dit gesprek vond al plaats": records an overdue step's conversation. */
export async function recordPastJourneyStep(
  userId: string,
  journeyId: string,
  stepId: string,
  { date, reportText }: HeldJourneyConversation,
): Promise<string> {
  const body: JourneyRecordPastRequest = { date, report_text: reportText };
  const response = await apiFetchJson<JourneyStepMeetingResponse>(
    `${stepPath(userId, journeyId, stepId)}/actions/record-past`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  return response.meeting_id;
}
